/**
 * WP-06C-A — Authenticated server entry points for the hosted Loop path.
 *
 * The actor is NEVER accepted from the caller: `requireSupabaseAuth` validates
 * the bearer token against Supabase Auth and puts the verified `userId` and an
 * RLS-scoped `supabase` client on the context. Creation uses the verified id as
 * the sole creator; reads use the RLS-scoped client so workspace isolation is
 * enforced by PostgreSQL, not by application code.
 *
 * The privileged creation adapter is imported INSIDE the handler so the
 * service-role client never enters a client-reachable module graph.
 *
 * Responses are the stable, non-sensitive outcome vocabularies defined by the
 * command and repository layers — never a provider message, SQL text, digest, or
 * raw exception.
 */

import { createServerFn } from "@tanstack/react-start";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  createLoopCommand,
  DETERMINISTIC_CREATION_IDENTITY,
  type LoopCreationOutcome,
} from "./creation/creationCommand";
import { validateCreationInput } from "./creation/creationRequest";
import {
  getHostedLoop,
  listHostedLoops,
  type LoopDetailOutcome,
  type LoopListOutcome,
} from "./read/loopRepository";
import { validateLoopIdInput } from "./read/readRequest";

export const createHostedLoop = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(validateCreationInput)
  .handler(async ({ data, context }): Promise<LoopCreationOutcome> => {
    // Guest-first access: an anonymous guest may converse and receive advisory
    // proposals, but durable Loop ownership requires an account. Refused here,
    // at the authority boundary, regardless of what the UI offered.
    if ((context.claims as { is_anonymous?: unknown }).is_anonymous === true) {
      return { ok: false, reason: "role_not_permitted", retryable: false };
    }
    const { supabaseLoopCreationPort } = await import("./creation/creationAdapter.server");
    return createLoopCommand(
      supabaseLoopCreationPort,
      { userId: context.userId },
      data.request,
      DETERMINISTIC_CREATION_IDENTITY,
    );
  });

export const listHostedLoopsCommand = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<LoopListOutcome> => listHostedLoops(context.supabase));

export const getHostedLoopCommand = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(validateLoopIdInput)
  .handler(async ({ data, context }): Promise<LoopDetailOutcome> =>
    getHostedLoop(context.supabase, data.loopId),
  );
